import { BankDealProfile, MilestoneReport } from '.';
import { StructureTranches, Tier1BankTypes, Tier3ContactTypes } from './TrancheEvaluation';

export interface ActiveDataRoomUser {
	id: number;
	guid: string;
	bankDealProfileGuid: string;
	bankContactGuid: string;
	fullName: string;
	bankName: string;
}

export interface ActiveDataRoomLender {
	dataRoomUserId: number;
	dataRoomUserGuid: string;
	bankDealProfileGuid: string;
	bankContactGuid: string;
	bankGuid: string;
	dataRoomGuid: string;
	dealGuid: string;
	fullName: string;
	bankName: string;
	emailAddress: string;
	hasTermSheets: boolean;
}

export interface DataRoomUser {
	id: number;
	guid: string;

	dataRoomId: number;
	bankDealProfileId: number;
	bankId: number;
	personId: number;
	userId: number;

	dataRoomGuid: string;
	bankDealProfileGuid: string;
	bankGuid: string;
	userGuid: string;

	bankName: string;
	firstName: string;
	lastName: string;
	emailAddress: string;
	phone: string;
	tier3ContactType: Tier3ContactTypes;
	tier3ContactTypeDescription: string;

	lenderScore?: number;
	lenderScoreLastUpdatedDate?: Date;
	isLockedOut: boolean;
	structureTranche?: StructureTranches;
	tier1?: Tier1BankTypes;

	lastAccessedOn?: Date;
	lastInviteSent?: Date;
	accessed: boolean;
	signedNda: boolean;
	allowAccess?: boolean;
	teamMemberCount: number;
	hasNda: boolean;
	optedOut: boolean;
	dealBankStatus: string;
	customNdaFileName: string;
	noteCount: number;

	readyForCall?: MilestoneStatusResponses | null;
	readyForCallOn?: Date | null;
	underwriting?: MilestoneStatusResponses | null;
	underwritingOn?: Date | null;
	parentDataRoomUserId: number | null;
	parentDataRoomUserGuid: string | null;
	isAssociatedContact: boolean;
	hasEnterpriseLoanWatchSubscription: boolean;
	bankDealProfile?: BankDealProfile;
	lenderScoreFlag: boolean;
	teamMembers: AssociatedTeamMember[];
}

export interface AssociatedTeamMember {
	id: number;
	guid: string;
	bankContactGuid: string;
	bankGuid: string;
	userGuid: string;
	bankName: string;
	firstName: string;
	lastName: string;
	emailAddress: string;
	phone: string;
	allowAccess: boolean;
	lastAccessedOn?: Date;
	contactType: AssociatedContactType;
	isLockedOut: boolean;
	parentDataRoomUserGuid: string | null;
}

export type AssociatedContactType = keyof typeof AssociatedContactTypeMap;
export const AssociatedContactTypeMap = {
	DataRoomUser: 'Data Room User',
	TeamMember: 'Team Member',
};

export type MilestoneStatusResponses = keyof typeof MilestoneStatusResponsesMap;
export const MilestoneStatusResponsesMap = {
	NotAnswered: 'Not Answered',
	Yes: 'Yes',
	OptOut: 'Out-Out',
	NotYet: 'Not Yet',
};

export interface DataRoomTeamMember {
	id: number;
	guid: string;

	bankContactTeamMemberId: number;
	bankContactId: number;
	bankId: number;
	personId: number;
	userId: number;
	dataRoomId: number;

	bankContactTeamMemberGuid: string;
	dataRoomGuid: string;
	bankContactGuid: string;
	bankGuid: string;
	userGuid: string;

	bankName: string;
	firstName: string;
	lastName: string;
	emailAddress: string;
	phone: string;

	allowAccess: boolean;
	lastAccessedOn?: Date;
	lastInviteSent?: Date;
	createdOn?: Date;

	dealStatus: string;
	isLockedOut: boolean;
}

export interface DataRoomContact {
	id: number;
	guid: string;

	personId: number;
	userId: number;
	dataRoomId: number;

	dataRoomGuid: string;
	userGuid: string;
	companyContactDataRoomGuid: string;

	bankName: string;
	firstName: string;
	lastName: string;
	emailAddress: string;
	phone: string;

	allowAccess: boolean;
	lastAccessedOn?: Date;
	lastInviteSent?: Date;
	createdOn?: Date;

	isLockedOut: boolean;
	isDealAdmin: boolean;
}

export interface SendInviteModel {
	subject: string;
	emailBody: string;
}

export interface DataRoomLenderStatusBar {
	roomUser: ActiveDataRoomUser;
	report: MilestoneReport;
	directConnectReport?: DirectConnectMilestoneReport;
}

export interface DirectConnectMilestoneReport {
	id: number;
	guid: string;
	dataRoomId: number;
	dataRoomGuid: string;
	dealGuid: string;
	bankId: number;
	bankGuid: string;
	bankContactId: number;
	bankContactGuid: string;
	personId: number;
	userId: number;
	userGuid: string;
	bankDealProfileId: number;
	bankDealProfileGuid: string;
	bankName: string;
	firstName: string;
	lastName: string;
	lenderName: string;
	emailAddress: string;
	phone: string;
	tier1Type?: Tier1BankTypes;
	tier3ContactType?: Tier3ContactTypes;
	tier3ContactTypeDescription: string;
	structureTranche?: StructureTranches;
	activeUser: boolean;

	roomDeclineReasons: string;
	roomDeclineReasonList: string[];
	otherRoomDeclineReason: string;
	roomReasonDetail: string;
	hasRoomOptOutReasons: boolean;

	otherDeclineReason: string;
	lenderDeclineReasons: string;
	lenderDeclineReasonList: string[];

	lastAccessedOn: Date | null;

	documentsDownloaded: MilestoneStatusResponses;
	documentsDownloadedDate: Date | null;

	termSheetSubmitted: MilestoneStatusResponses;
	termSheetSubmittedOn: Date | null;

	underwriting: MilestoneStatusResponses;
	underwritingOn: Date | null;

	funded: MilestoneStatusResponses;
	dateFunded: Date | null;
	amountFunded: number | null;

	dealBankStatus: string;
	noteCount: number;
	optedOut: boolean;
	isLockedOut: boolean;
	allowAccess?: boolean;
	signedNda: boolean;
}

export type DirectConnectMilestoneType = keyof typeof DirectConnectMilestoneTypeMap;
export const DirectConnectMilestoneTypeMap = {
	DocumentsReviewed: 'Documents Reviewed',
	TermSheetSubmitted: 'Term Sheet Submitted',
	Underwriting: 'Underwriting',
	Funded: 'Funded',
};

export interface UpdateDirectConnectMilestoneModel {
	milestoneType: DirectConnectMilestoneType;
	amountFunded?: number | null;
}

export interface ContactBorrowerModel {
	subject: string;
	emailBody: string;
}
